import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { DraggableBlob, Module, MymeEditorType } from '@myme/utils';
import { useDebounce, usePagination, useTranslation } from 'src/hooks';
import { toast, ToastType } from 'src/utils';
import { addNewModule, editModuleName } from './helpers';
import { useEditMymeContent } from './useEditMymeContent';
import { useGetMappedBlobs } from './useGetMappedBlobs';
import { useMymeEditor } from '../context';

interface BlobsLibraryPagination {
  page: number;
  handleChangeBlobsPage: (page: number) => void;
  pageCount: number;
}

export type BlobsWithoutSetLocation = {
  moduleId: string;
  blobId: string;
}[];

interface ModulesContextValue {
  modules: Module[];
  mymeType: MymeEditorType;
  areModulesEdited: boolean;
  setAreModulesEdited: React.Dispatch<React.SetStateAction<boolean>>;
  searchField: string;
  setSearchField: React.Dispatch<React.SetStateAction<string>>;
  blobs: DraggableBlob[];
  areBlobsLoading: boolean;
  blobsPagination: BlobsLibraryPagination;
  isEditing: boolean;
  setModules: React.Dispatch<React.SetStateAction<Module[]>>;
  handleAddNewModule: () => void;
  handleEditModuleName: (moduleId: string, moduleName: string) => void;
  handleDeleteModule: (moduleId: string) => void;
  handleUpdateMymeContent: (props: { blockNavigation?: boolean; publishMyme?: boolean }) => void;
  blobsWithoutSetLocation: BlobsWithoutSetLocation;
  setBlobsWithoutSetLocation: React.Dispatch<React.SetStateAction<BlobsWithoutSetLocation>>;
}

export const Context = createContext<ModulesContextValue | undefined>(undefined);
interface ProviderProps {
  children: ReactNode;
}

export const ContentContextProvider = ({ children }: ProviderProps) => {
  const {
    editor: { myme }
  } = useMymeEditor();
  const [modules, setModules] = useState<Module[]>([]);
  const [areModulesEdited, setAreModulesEdited] = useState(false);
  const [searchField, setSearchField] = useState('');
  const debouncedSearchField = useDebounce(searchField, 500);
  const { pagination, handleChangePage, getLastPageNumber } = usePagination({ page: 1, pageSize: 8 });
  const { blobs, areBlobsLoading, blobsTotal } = useGetMappedBlobs(pagination, debouncedSearchField);
  const { editMymeContentMutation } = useEditMymeContent(modules, setAreModulesEdited);
  const [blobsWithoutSetLocation, setBlobsWithoutSetLocation] = useState<BlobsWithoutSetLocation>([]);

  const { t } = useTranslation();

  useEffect(() => {
    handleChangePage(1);
  }, [debouncedSearchField]);

  useEffect(() => {
    if (!areModulesEdited) {
      setModules(myme.modules);
    }
  }, [myme.modules]);

  const handleUpdateMymeContent = async ({
    blockNavigation,
    publishMyme
  }: {
    blockNavigation?: boolean;
    publishMyme?: boolean;
  }) => {
    const updatedBlobWithNoLocationError: BlobsWithoutSetLocation = [];
    if (myme.type === 'LOCATION_BASED') {
      modules.forEach(({ blobs, id: moduleId }) => {
        blobs.forEach(({ location, id: blobId }) => {
          if (!location?.latitude && !location?.longitude) {
            updatedBlobWithNoLocationError.push({ moduleId, blobId });
          }
        });
      });

      if (updatedBlobWithNoLocationError.length > 0) {
        setBlobsWithoutSetLocation(updatedBlobWithNoLocationError);
        toast(ToastType.ERROR, t('pages.mymes.editor.blobs.no_location_error'));
        return;
      }
    }

    editMymeContentMutation.mutate({ blockNavigation, publishMyme });
  };

  const handleAddNewModule = () => {
    const newItemLabelIndex = modules.length + 1;
    setModules(prevState => [...prevState, addNewModule(newItemLabelIndex)]);
    setAreModulesEdited(true);
  };

  const handleEditModuleName = (moduleId: string, moduleName: string) => {
    const newModulesState = editModuleName(moduleId, moduleName);
    setModules(newModulesState);
    setAreModulesEdited(true);
  };

  const handleDeleteModule = (moduleId: string) => {
    setModules(prevState => prevState.filter(module => module.id !== moduleId));
    if (modules.find(module => module.isNew)) {
      setAreModulesEdited(true);
    }
  };

  return (
    <Context.Provider
      value={{
        modules,
        mymeType: myme.type,
        blobs,
        areBlobsLoading,
        areModulesEdited,
        searchField,
        setSearchField,
        setAreModulesEdited,
        setBlobsWithoutSetLocation,
        blobsPagination: {
          page: pagination.page,
          pageCount: getLastPageNumber(blobsTotal),
          handleChangeBlobsPage: handleChangePage
        },
        isEditing: editMymeContentMutation.isLoading,
        blobsWithoutSetLocation,
        setModules,
        handleAddNewModule,
        handleEditModuleName,
        handleDeleteModule,
        handleUpdateMymeContent
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useContentContext = () => {
  const { t } = useTranslation();
  const contentContext = useContext(Context);

  if (contentContext === undefined) {
    throw new Error(t('errors.validation.context', { contextName: 'Context' }));
  }

  return contentContext;
};
